import { ChangeDetectionStrategy, Component, computed, input, ViewEncapsulation } from '@angular/core';
import { FwPopoverComponent } from '@flywheel-io/vision';
import cronstrue from 'cronstrue';

import { XferExport, XferImport } from '../../../shared/models/xfer.model';
import { isExternalStorage } from '../../../shared/projects.utils';
import { OperationPopoverFields } from './operation-popover.enums';

const FIELD_LABELS: Partial<Record<OperationPopoverFields, string>> = {
  [OperationPopoverFields.Destination]: 'Destination',
  [OperationPopoverFields.DestinationUrl]: 'Destination URL',
  [OperationPopoverFields.ExportJobName]: 'Export Job Name',
  [OperationPopoverFields.ImportJobName]: 'Import Job Name',
  [OperationPopoverFields.JobId]: 'Job ID',
  [OperationPopoverFields.JobSchedule]: 'Job Schedule (Cron)',
  [OperationPopoverFields.JobScheduleId]: 'Job Schedule ID',
  [OperationPopoverFields.ScheduleEnd]: 'Schedule End',
  [OperationPopoverFields.ScheduleStart]: 'Schedule Start',
  [OperationPopoverFields.Source]: 'Source',
  [OperationPopoverFields.SourceUrl]: 'Source URL',
  [OperationPopoverFields.Type]: 'Type',
};

@Component({
  host: { 'test-id': 'operation-popover' },
  selector: 'operation-popover',
  styleUrls: ['./operation-popover.component.scss'],
  templateUrl: './operation-popover.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class OperationPopoverComponent {
  public title = input<string>();
  public maxWidth = input('400px');
  public config = input<OperationPopoverFields[]>();
  public operation = input<XferExport | XferImport>();

  protected readonly dateMask = 'yyyy/MM/dd hh:mm';
  protected readonly OperationPopoverFields = OperationPopoverFields;
  protected readonly dateFields = [OperationPopoverFields.ScheduleStart, OperationPopoverFields.ScheduleEnd];

  protected data = computed(() => {
    const op = this.operation();

    return op ? this.mapData(op) : ({} as Partial<Record<OperationPopoverFields, string>>);
  });

  protected humanReadableCron = computed(() => {
    const cron = this.data()[OperationPopoverFields.JobSchedule];

    return cron ? cronstrue.toString(cron) : '';
  });

  private readonly popoverClass = 'fw-popover-panel';
  private readonly idFields = [OperationPopoverFields.OriginId, OperationPopoverFields.SourceId, OperationPopoverFields.DestinationId];
  private readonly fieldsWithTooltip = [
    OperationPopoverFields.ExportJobName,
    OperationPopoverFields.ImportJobName,
    OperationPopoverFields.SourceUrl,
  ];

  private mapData(operation: XferExport | XferImport): Partial<Record<OperationPopoverFields, string>> {
    const { schedule, storage, origin, label, _id } = operation;

    const externalStorage = isExternalStorage(storage?._id);
    const url = externalStorage ? operation.storage_url : storage?.url;
    const data: Partial<Record<OperationPopoverFields, string>> = {
      [OperationPopoverFields.ImportJobName]: label,
      [OperationPopoverFields.ExportJobName]: label,
      [OperationPopoverFields.JobId]: _id,
      [OperationPopoverFields.Source]: storage?.label,
      [OperationPopoverFields.SourceUrl]: url,
      [OperationPopoverFields.Destination]: storage?.label,
      [OperationPopoverFields.DestinationUrl]: url,
      [OperationPopoverFields.Type]: origin.type,
      [OperationPopoverFields.OriginId]: origin.id,
    };

    if (externalStorage) {
      data[OperationPopoverFields.SourceId] = storage?._id;
      data[OperationPopoverFields.DestinationId] = storage?._id;
    }

    if (schedule) {
      data[OperationPopoverFields.JobScheduleId] = schedule._id;
      data[OperationPopoverFields.JobSchedule] = schedule.cron;

      if (schedule?.start) {
        data[OperationPopoverFields.ScheduleStart] = schedule.start;
      }

      if (schedule?.end) {
        data[OperationPopoverFields.ScheduleEnd] = schedule.end;
      }
    }

    return data;
  }

  protected getFieldLabel(field: OperationPopoverFields): string {
    if (this.idFields.includes(field)) {
      return 'ID';
    }

    return FIELD_LABELS[field] ?? '';
  }

  protected isTooltip(field: OperationPopoverFields): boolean {
    return this.fieldsWithTooltip.includes(field);
  }

  protected onMouseHover(): void {}

  protected onMouseLeave(event: MouseEvent, popover: FwPopoverComponent): void {
    const relatedTarget = event.relatedTarget as HTMLElement;

    if (!relatedTarget?.classList.contains(this.popoverClass)) {
      popover.handlePanelLeave();
    }
  }
}
